import type * as React from "react";

import { cn } from "../lib/cn";

/*
 * How the pin is drawn, and why.
 *
 * Three parts, painted back to front: the metal needle, the plastic shoulder it
 * comes out of, and the domed head over both. The head is the only part with
 * real size, so it carries the lighting; the other two exist to say the head is
 * pushed into something rather than floating on it.
 *
 * The head is 15x12, not a circle. A ball with a stick under it reads as a
 * lollipop; what makes a pushpin a pushpin is that the head is wider than it is
 * tall - a squat dome, seen from slightly above, which is the angle everything
 * else on this desk is drawn from. That single proportion did more for the read
 * than any amount of gradient work.
 *
 * Light comes from above and slightly left, the same direction LabelTape's
 * emboss and the clipboard's clip are lit from, so a board carrying all three
 * has one sun. Three gradients over a token base shape the dome: a tight
 * specular where the plastic catches the light, a weak bounce along the lower
 * left, and a broad darkening lower right where the dome turns away. None of
 * them reaches the edge, so the base color still reads as the pin's color
 * rather than as a gradient; the 1px darker rim and the inner bottom shade are
 * what make the shape read as domed rather than as a flat disc.
 *
 * The base is the stopped red warmed with gold: at full --color-ink-stopped the
 * head would match the urgent status ink exactly, and warmed it reads as its
 * own red plastic. A pin is not a status - it says "this is on the board",
 * never "something is wrong" - so it must not look like one.
 *
 * rgba() appears here rather than tokens because this is light on an object,
 * not palette (DESIGN.md's LabelTape / clipboardChrome precedent). Every hue is
 * a token or a mix of two.
 */
const DOME = cn(
  "relative block h-3 w-[15px] rounded-full",
  "bg-[color-mix(in_srgb,var(--color-ink-stopped)_74%,var(--color-gold))]",
  "[background-image:radial-gradient(ellipse_20%_26%_at_33%_23%,rgba(255,255,255,0.98),rgba(255,255,255,0.28)_55%,rgba(255,255,255,0)_85%),radial-gradient(ellipse_55%_40%_at_28%_90%,rgba(255,255,255,0.26),rgba(255,255,255,0)_60%),radial-gradient(ellipse_70%_65%_at_74%_88%,rgba(0,0,0,0.5),rgba(0,0,0,0.12)_45%,rgba(0,0,0,0)_78%)]",
  // rim, inner bottom shade, then the cast shadow: contact first, soft after.
  "shadow-[inset_0_0_0_1px_rgba(46,35,24,0.22),inset_0_-1px_2px_rgba(46,35,24,0.3),0_1px_1px_rgba(46,35,24,0.3),0_2px_4px_rgba(46,35,24,0.35)]",
);

/*
 * The shoulder: the darker plastic under the head, all but ~1.5px of it hidden
 * behind the dome. It is what keeps the needle from looking stuck straight into
 * the head, and it is deliberately barely there - any more of it and the pin
 * grows a neck.
 */
const SHOULDER = cn(
  "absolute left-1/2 top-[45%] h-2 w-[7px] -translate-x-1/2 rounded-b-[2px]",
  "bg-[color-mix(in_srgb,var(--color-ink-stopped)_55%,var(--color-ink))]",
  "bg-[linear-gradient(90deg,rgba(255,255,255,0.22),rgba(255,255,255,0.02)_45%,rgba(0,0,0,0.25))]",
);

/*
 * The needle: a 3px cylinder from the dome's center down, so ~4px of it shows
 * below the head - the bit that has not gone in. Brushed metal is the clip's
 * recipe (token base, rgba lighting across it), and its own small shadow is the
 * contact shadow where the pin enters the surface.
 */
const NEEDLE = cn(
  "absolute left-1/2 top-1/2 h-2.5 w-[3px] -translate-x-1/2",
  "bg-faint",
  "bg-[linear-gradient(90deg,rgba(255,255,255,0.15),rgba(255,255,255,0.7)_30%,rgba(46,35,24,0.45))]",
  "shadow-[0_1px_2px_rgba(46,35,24,0.4)]",
);

export interface PinProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * A pushpin, head-on, stuck through whatever it sits on: a 15x12 domed head
 * with the needle showing under it and a small shadow on the surface below.
 * Decorative and inert (aria-hidden, no pointer events) - it says the object is
 * on the board, and nothing about the object's state.
 *
 * Positioned absolutely at the top-center of the nearest positioned ancestor,
 * which is where a pin goes on a card. Pass `className` to move it (a corner, an
 * inset) - anything that sets `left`/`top`/`translate` wins, since it lands
 * after these classes.
 *
 * Pins belong on cork. A pin through a sheet lying on a desk or a page is a pin
 * through the desk: if the surface behind the object is not a board, the object
 * does not take one.
 */
export function Pin({ className, ...rest }: PinProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute left-1/2 top-0 block h-3 w-[15px] -translate-x-1/2 -translate-y-1/2",
        className,
      )}
      {...rest}
    >
      <span className={NEEDLE} />
      <span className={SHOULDER} />
      <span className={DOME} />
    </span>
  );
}
